import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { ArrowLeft, UploadCloud, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { db } from '../../db/shiksha-db';
import { useAuth } from '../../hooks/useAuth';

// Mandatory and optional system fields — includes class and section
const SYSTEM_FIELDS = [
  { id: 'rollNo', label: 'Roll No', required: true },
  { id: 'studentName', label: 'Student Name', required: true },
  { id: 'class', label: 'Class', required: false },
  { id: 'section', label: 'Section', required: false },
  { id: 'dob', label: 'Date of Birth (DOB)', required: false },
  { id: 'guardianName', label: 'Father/Guardian Name', required: false },
  { id: 'motherName', label: "Mother's Name", required: false },
  { id: 'gender', label: 'Gender', required: false },
];

export function UdiseIngestion() {
  const { user } = useAuth();
  const [csvData, setCsvData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [sampleData, setSampleData] = useState([]);
  const [mapping, setMapping] = useState({});
  const [errors, setErrors] = useState([]);
  const [isImported, setIsImported] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.data.length > 0) {
            const detectedHeaders = Object.keys(results.data[0]);
            setHeaders(detectedHeaders);
            setSampleData(results.data.slice(0, 3)); // Keep top 3 rows for sample
            setCsvData(results.data);

            // Heuristic Mapping (improved with class/section/dob/gender detection)
            const initialMapping = {};
            detectedHeaders.forEach(header => {
              const hLower = header.toLowerCase().trim();

              if ((hLower.includes('name') && !hLower.includes('guardian') && !hLower.includes('father') && !hLower.includes('mother')) ||
                  hLower === 'student' || hLower === 'student_name') {
                initialMapping[header] = 'studentName';
              } else if (hLower.includes('roll') || hLower === 'id' || hLower === 'sr' || hLower === 'sl' || hLower === 'sr_no' || hLower === 'sl_no') {
                initialMapping[header] = 'rollNo';
              } else if (hLower.includes('dob') || hLower.includes('birth') || hLower.includes('date_of_birth')) {
                initialMapping[header] = 'dob';
              } else if (hLower.includes('guardian') || hLower.includes('father')) {
                initialMapping[header] = 'guardianName';
              } else if (hLower.includes('mother')) {
                initialMapping[header] = 'motherName';
              } else if (hLower === 'class' || hLower === 'grade' || hLower === 'standard' || hLower === 'std') {
                initialMapping[header] = 'class';
              } else if (hLower === 'section' || hLower === 'sec' || hLower === 'div') {
                initialMapping[header] = 'section';
              } else if (hLower === 'gender' || hLower === 'sex') {
                initialMapping[header] = 'gender';
              }
            });
            setMapping(initialMapping);
            validateMapping(initialMapping);
          }
        }
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    multiple: false
  });

  const validateMapping = (currentMapping) => {
    const mappedValues = Object.values(currentMapping);
    const newErrors = [];

    SYSTEM_FIELDS.filter(f => f.required).forEach(field => {
      if (!mappedValues.includes(field.id)) {
        newErrors.push(`Missing mandatory mapping: ${field.label}`);
      }
    });

    setErrors(newErrors);
  };

  const handleMappingChange = (header, systemField) => {
    const newMapping = { ...mapping };
    if (systemField) {
      newMapping[header] = systemField;
    } else {
      delete newMapping[header];
    }
    setMapping(newMapping);
    validateMapping(newMapping);
  };

  const handleImport = async () => {
    if (errors.length > 0) return;

    try {
      const studentsToInsert = csvData.map(row => {
        const student = {};
        for (const [csvHeader, systemField] of Object.entries(mapping)) {
          let value = row[csvHeader];
          if (systemField === 'rollNo') {
            value = parseInt(value, 10);
            if (isNaN(value)) value = 0;
          }
          // Map 'studentName' field to 'name' in DB
          if (systemField === 'studentName') {
            student.name = value;
          } else {
            student[systemField] = value;
          }
        }

        // Default fallbacks if not mapped
        if (!student.class) student.class = '';
        if (!student.section) student.section = '';
        
        student.syncStatus = 'PENDING';
        student.updatedAt = new Date().toISOString();

        return student;
      }).filter(s => s.rollNo && s.name); // basic validation

      // Upsert logic: merge by rollNo + class + section
      const existingStudents = await db.students.toArray();
      const existingMap = new Map(
        existingStudents.map(s => [`${s.rollNo}|${s.class}|${s.section}`, s])
      );

      const toUpsertMap = new Map();
      let dupesUpdated = 0;
      let newAdded = 0;

      for (const s of studentsToInsert) {
        const key = `${s.rollNo}|${s.class}|${s.section}`;
        
        // If the student already exists in DB or was processed earlier in the CSV, merge it
        if (existingMap.has(key) || toUpsertMap.has(key)) {
          const base = toUpsertMap.get(key) || existingMap.get(key);
          // Overwrite base with 's', but preserve the original ID
          const merged = { ...base, ...s, id: base.id || s.id || crypto.randomUUID() }; 
          toUpsertMap.set(key, merged);
          if (existingMap.has(key)) dupesUpdated++;
        } else {
          s.id = crypto.randomUUID(); // Stable UUID for new students
          toUpsertMap.set(key, s);
          newAdded++;
        }
      }

      const finalUpsertArray = Array.from(toUpsertMap.values());
      if (finalUpsertArray.length > 0) {
        await db.students.bulkPut(finalUpsertArray);
        
        // PUSH TO CLOUDFLARE WORKER
        const idToken = await user.getIdToken();
        const apiUrl = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
        const res = await fetch(`${apiUrl}/api/admin/students`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ students: finalUpsertArray })
        });
        
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(`Cloud Sync Failed: ${errData.error || res.statusText}`);
        }
      }

      setImportCount(newAdded);
      setDuplicateCount(dupesUpdated);
      setIsImported(true);
    } catch (err) {
      setErrors([`Database error: ${err.message}`]);
    }
  };

  const handleCancel = () => {
    setCsvData(null);
    setHeaders([]);
    setSampleData([]);
    setMapping({});
    setErrors([]);
  };

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink">UDISE+ Ingestion Engine</h1>
            <p className="text-sm text-graphite font-secondary">Import student ledgers from government CSVs.</p>
          </div>
        </div>

        {isImported ? (
          <div className="bg-white border border-divider rounded-xl p-12 shadow-sm flex flex-col items-center">
            <CheckCircle size={64} className="text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-ink mb-2">Import Successful</h2>
            <p className="text-graphite mb-2">Imported {importCount} new student records into the ledger.</p>
            {duplicateCount > 0 && (
              <p className="text-sm text-indigo-600 mb-6">
                Updated {duplicateCount} existing records (merged latest details from CSV).
              </p>
            )}
            <button
              onClick={() => { setIsImported(false); handleCancel(); setImportCount(0); setDuplicateCount(0); }}
              className="bg-indigo-600 text-white rounded-lg px-6 py-2 font-semibold hover:bg-indigo-700"
            >
              Import Another File
            </button>
          </div>
        ) : !csvData ? (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-white hover:border-indigo-400 hover:bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            <UploadCloud size={48} className="mx-auto text-indigo-500 mb-4" />
            <h3 className="text-lg font-bold text-ink mb-1">Drop UDISE+ CSV Here</h3>
            <p className="text-sm text-graphite font-secondary">Or click to select a file from your computer</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Error Banner */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-conflict flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-conflict text-sm">Cannot Import</h4>
                  <ul className="list-disc pl-5 mt-1">
                    {errors.map((err, i) => (
                      <li key={i} className="text-sm text-red-700">{err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Mapping Table */}
            <div className="bg-white border border-divider rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-divider flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-graphite" />
                <span className="font-semibold text-sm text-ink">Heuristic Mapping Table</span>
                <span className="text-xs text-graphite ml-auto">{csvData?.length || 0} rows detected</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-divider bg-gray-50/50">
                      <th className="p-4 text-sm font-semibold text-ink w-1/3">Detected CSV Header</th>
                      <th className="p-4 text-sm font-semibold text-ink w-1/3">System Field</th>
                      <th className="p-4 text-sm font-semibold text-ink w-1/3">Sample Data</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {headers.map(header => (
                      <tr key={header} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4 text-sm font-medium text-ink bg-gray-50/50">
                          {header}
                        </td>
                        <td className="p-4">
                          <select
                            value={mapping[header] || ''}
                            onChange={(e) => handleMappingChange(header, e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                          >
                            <option value="">-- Ignore Column --</option>
                            {SYSTEM_FIELDS.map(field => (
                              <option key={field.id} value={field.id}>
                                {field.label} {field.required ? '*' : ''}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-4 text-sm text-graphite font-mono bg-gray-50/50">
                          {sampleData[0]?.[header] || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={errors.length > 0}
                className="px-6 py-2 bg-indigo-600 rounded-lg text-sm font-semibold text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <CheckCircle size={16} />
                Confirm & Import to Ledger
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
